import { BaseTool, ToolContext } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BIMElement } from '../../core/types';
import { computeWallMoveUpdates } from '../../utils/WallGeometryUtils';
import { buildSelectionSteps, SelectionStep } from '../../utils/elementChains';
import { sceneSourceOf, transformSceneObjects } from '../../core/scene';
import { useDocumentStore } from '../../state/useDocumentStore';
import { useAppStore } from '../../state/store';
import { entityMeshRegistry } from '../../rendering/toolEventBus';
import { resolveVisibility, currentVisibilityInput } from '../../rendering/visibility';

export class SelectToolClass extends BaseTool {
    id = 'SELECT';
    name = 'Select';
    cursor = 'default';

    private isBoxSelecting = false;
    private isDragging = false;
    private clickedElementId: string | null = null;
    private static readonly DRAG_THRESHOLD_PX = 4;
    private boxStartPoint: { x: number; y: number } | null = null;
    private boxCurrentPoint: { x: number; y: number } | null = null;

    // Drag-to-move gesture (armed by a plain press on an element)
    private moveCandidate = false;
    private isMoveDragging = false;
    private pressedElementId: string | null = null;
    private pressedWasSelected = false;
    private moveElementIds: string[] = [];
    private moveBasePoint: THREE.Vector3 | null = null;
    private moveCurrentPoint: THREE.Vector3 | null = null;
    private moveStartScreen: { x: number; y: number } | null = null;

    // BIM hover/Tab cycling: ordered steps (single element / connected
    // chain) under the last cursor position. cycleIndex === -1 means no
    // hover stack is active.
    private lastPointerClient: { x: number; y: number } | null = null;
    private cycleSteps: SelectionStep[] = [];
    private cycleIndex = -1;

    activate(context: ToolContext): void {
        super.activate(context);
        console.log('[SelectTool] Activated');
    }

    deactivate(): void {
        this.resetMoveGesture();
        // Cycle state must drop while the context is still alive, otherwise
        // the hover highlight and status label would leak into the next tool.
        this.clearCycleState();
        super.deactivate();
        this.resetBoxState();
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return; // Only left click

        // A live Tab-cycle preview commits the highlighted step first
        // (BIM: click finalizes whatever Tab put under the blue highlight).
        if (this.cycleIndex >= 0 && !event.ctrlKey && !event.shiftKey && !event.metaKey) {
            const step = this.cycleSteps[this.cycleIndex];
            if (step) {
                this.context.appStore.select([...step.ids]);
                this.context.bimStore.setSelection([...step.ids]);
                this.clearCycleState();
                return;
            }
        }

        // Modifier press starts a box selection, never a move drag
        if (event.ctrlKey || event.shiftKey || event.metaKey) {
            this.beginBoxTracking(event);
            return;
        }

        const elementId = this.findElementAtPoint(event);

        if (elementId) {
            // Arm a potential move drag; the click selection commits on
            // pointer-up so a press-drag can still become a move gesture.
            const selection = this.context.appStore.selectedIds;
            this.pressedElementId = elementId;
            this.pressedWasSelected = selection.includes(elementId);
            this.moveCandidate = this.isMovableElement(elementId);
            this.moveStartScreen = this.getScreenPoint(event);
            this.moveBasePoint = this.getWorkPlanePoint(event);
            if (this.moveBasePoint) {
                this.moveBasePoint = this.snapToGrid(this.moveBasePoint);
            }
            return;
        }

        // Press on empty space: fall through to box-selection tracking
        this.beginBoxTracking(event);
    }

    private beginBoxTracking(event: ThreeEvent<PointerEvent>): void {
        const rect = this.context!.renderer.domElement.getBoundingClientRect();
        this.boxStartPoint = {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
        };
        this.boxCurrentPoint = null;
        this.isBoxSelecting = true;
        this.isDragging = false;
        // Resolve the element under the pointer now; modifier clicks commit
        // the selection on pointer-up so a press-drag can still become a box.
        this.clickedElementId = this.findElementAtPoint(event);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Live drag-to-move: translate the armed selection with the cursor
        if (this.moveCandidate && this.moveBasePoint) {
            if (!this.isMoveDragging) {
                const screen = this.getScreenPoint(event);
                if (this.moveStartScreen && this.exceedsDragThreshold(this.moveStartScreen, screen)) {
                    this.beginMoveDrag(event);
                } else {
                    return;
                }
            }
            this.updateMoveDrag(event);
            return;
        }

        if (!this.isBoxSelecting || !this.boxStartPoint) {
            // Idle hover: track the overlapping-element stack for Tab cycling
            this.updateHoverCycle(event);
            return;
        }

        const rect = this.context.renderer.domElement.getBoundingClientRect();
        this.boxCurrentPoint = {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
        };

        if (!this.isDragging) {
            const dx = this.boxCurrentPoint.x - this.boxStartPoint.x;
            const dy = this.boxCurrentPoint.y - this.boxStartPoint.y;
            const threshold = SelectToolClass.DRAG_THRESHOLD_PX;
            if (dx * dx + dy * dy <= threshold * threshold) return;
            this.isDragging = true;
            // Box drag begins: reset selection unless modifier keys extend it
            if (!event.ctrlKey && !event.shiftKey && !event.metaKey) {
                this.context.appStore.select([]);
                this.context.bimStore.setSelection([]);
            }
        }

        this.context.appStore.setToolPreview({
            selectionBox: {
                start: this.boxStartPoint,
                current: this.boxCurrentPoint
            }
        });
    }

    onPointerUp(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.isMoveDragging) {
            // Drag finished: commit the moved geometry to the model
            this.commitMove();
        } else if (this.pressedElementId) {
            // Clean press-release without drag: treat as a selection click
            // (covers both movable and non-movable pressed elements)
            this.handleElementClick(this.pressedElementId, event);
        } else if (this.isBoxSelecting) {
            if (this.isDragging && this.boxCurrentPoint) {
                // Drag finished: apply window/crossing box selection
                this.completeBoxSelection(event);
            } else if (this.clickedElementId) {
                // Clean click on an element: select it
                this.handleElementClick(this.clickedElementId, event);
            } else if (!event.ctrlKey && !event.shiftKey && !event.metaKey) {
                // Clean click on empty space: clear selection
                this.context.appStore.select([]);
                this.context.bimStore.setSelection([]);
            }
        }

        this.resetBoxState();
        this.resetMoveGesture();
        this.context.appStore.setToolPreview(null);
    }

    onKeyDown(event: KeyboardEvent): void {
        // BIM-style hover cycling: Tab walks the overlapping-element stack
        // under the cursor, Shift+Tab walks it backwards. Skip when a text
        // field or modal dialog owns the keyboard so Tab stays accessible.
        if (event.key === 'Tab' && this.isKeyboardTargetSafe(event)) {
            event.preventDefault();
            this.cycleSelection(event.shiftKey ? -1 : 1);
            return;
        }
        super.onKeyDown(event);
        if (event.key === 'Escape') {
            // Escape drops the Tab-cycle preview back to plain hover state
            this.clearCycleState();
        }
        if (event.key === 'Escape' && this.isMoveDragging) {
            // Abort the drag: restore the geometry captured at drag start
            this.cancelMove();
        }
    }

    private isKeyboardTargetSafe(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement | null;
        if (!target || typeof target.closest !== 'function') return true;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            return false;
        }
        return !target.closest('[role="dialog"]');
    }

    // ==================== BIM Tab Cycling ====================

    /**
     * Idle hover tracking: capture the cursor position and rebuild the
     * overlapping-element stack so Tab can walk it. The top element also
     * drives the status-bar label ("Category : Type").
     */
    private updateHoverCycle(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const { clientX, clientY } = event.nativeEvent;

        // Coarse movement threshold: full-scene raycasts on every mousemove
        // are expensive on big models; sub-2px drift cannot change the stack.
        if (this.lastPointerClient) {
            const dx = clientX - this.lastPointerClient.x;
            const dy = clientY - this.lastPointerClient.y;
            if (dx * dx + dy * dy < 4) return;
        }
        this.lastPointerClient = { x: clientX, y: clientY };

        const ids = this.collectElementIdsAtPointer(clientX, clientY);
        this.cycleSteps = buildSelectionSteps(
            this.context.bimStore.project.elements,
            ids
        );
        // A pointer move always drops an in-progress Tab preview
        if (this.cycleIndex >= 0) {
            this.cycleIndex = -1;
            this.context.appStore.setToolPreview(null);
        }
        const topId = ids[0] ?? null;
        this.applyHoverFeedback(topId);
    }

    /** Reflect the plain hover state into the highlight and status bar. */
    private applyHoverFeedback(topId: string | null): void {
        if (!this.context) return;
        const element = topId
            ? this.context.bimStore.project.elements.get(topId)
            : null;
        const label = element ? `${element.category} : ${element.type}` : null;

        // Equal-value guards: pointermove storms must not spam the store
        // with no-op writes on every frame.
        if (this.context.appStore.hoveredId !== topId) {
            this.context.appStore.setHovered(topId);
        }
        if (this.context.appStore.statusText !== label) {
            this.context.appStore.setStatusText(label);
        }
    }

    /**
     * Advance the hover-stack index and preview the highlighted step.
     * Direction +1 = Tab, -1 = Shift+Tab.
     */
    private cycleSelection(direction: number): void {
        if (!this.context) return;

        // Keyboard-only flow (no mouse moves yet): build the stack from the
        // last known cursor position.
        if (this.cycleSteps.length === 0 && this.lastPointerClient) {
            const ids = this.collectElementIdsAtPointer(
                this.lastPointerClient.x,
                this.lastPointerClient.y
            );
            this.cycleSteps = buildSelectionSteps(
                this.context.bimStore.project.elements,
                ids
            );
        }
        if (this.cycleSteps.length === 0) return;

        const count = this.cycleSteps.length;
        if (this.cycleIndex < 0) {
            // Leaving plain hover: forward Tab jumps to the next step (the
            // connected chain when one exists), backward stays on the top.
            this.cycleIndex = direction > 0 && count > 1 ? 1 : 0;
        } else {
            this.cycleIndex = ((this.cycleIndex + direction) % count + count) % count;
        }
        this.applyCyclePreview(this.cycleSteps[this.cycleIndex]);
    }

    /** Show the blue cycle highlight + status-bar label for the active step. */
    private applyCyclePreview(step: SelectionStep): void {
        if (!this.context) return;
        const elements = this.context.bimStore.project.elements;
        const primary = elements.get(step.ids[0]);

        this.context.appStore.setHovered(step.kind === 'element' ? step.ids[0] : null);
        this.context.appStore.setStatusText(
            primary
                ? step.kind === 'element'
                    ? `${primary.category} : ${primary.type}`
                    : `${primary.category} : Chain (${step.ids.length} elements)`
                : null
        );
        this.context.appStore.setToolPreview({
            selectionHighlight: { ids: [...step.ids], kind: step.kind },
        });
    }

    /** Drop all cycling state (deactivation, Escape, committed selection). */
    private clearCycleState(): void {
        this.cycleSteps = [];
        this.cycleIndex = -1;
        if (this.context) {
            this.context.appStore.setHovered(null);
            this.context.appStore.setStatusText(null);
            this.context.appStore.setToolPreview(null);
        }
    }

    private resetBoxState(): void {
        this.isBoxSelecting = false;
        this.isDragging = false;
        this.clickedElementId = null;
        this.boxStartPoint = null;
        this.boxCurrentPoint = null;
    }

    private resetMoveGesture(): void {
        this.moveCandidate = false;
        this.isMoveDragging = false;
        this.pressedElementId = null;
        this.pressedWasSelected = false;
        this.moveElementIds = [];
        this.moveBasePoint = null;
        this.moveCurrentPoint = null;
        this.moveStartScreen = null;
        if (this.context) {
            this.context.renderer.domElement.style.cursor = this.cursor;
        }
    }

    private getScreenPoint(event: ThreeEvent<PointerEvent>): { x: number; y: number } {
        const rect = this.context!.renderer.domElement.getBoundingClientRect();
        return {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
        };
    }

    private exceedsDragThreshold(
        a: { x: number; y: number },
        b: { x: number; y: number }
    ): boolean {
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const threshold = SelectToolClass.DRAG_THRESHOLD_PX;
        return dx * dx + dy * dy > threshold * threshold;
    }

    // ==================== Drag-to-Move ====================

    private beginMoveDrag(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.pressedElementId) return;

        const clickedId = this.pressedElementId;
        if (!this.pressedWasSelected) {
            // A plain press on an unselected element drags just that element
            this.context.appStore.select([clickedId]);
            this.context.bimStore.setSelection([clickedId]);
        }
        const selection = [...this.context.appStore.selectedIds];
        const movable = selection.filter((id) => this.isMovableElement(id));
        if (movable.length === 0 || !this.moveBasePoint) return;

        this.moveElementIds = movable;
        this.isMoveDragging = true;
        this.moveCandidate = false;
        this.context.renderer.domElement.style.cursor = 'move';
        this.updateMoveDrag(event);
    }

    private updateMoveDrag(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getWorkPlanePoint(event);
        if (!point) return;

        this.moveCurrentPoint = this.snapToGrid(point);
        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            moveDrag: true,
            basePoint: this.moveBasePoint,
            current: this.moveCurrentPoint
        });
    }

    private commitMove(): void {
        if (!this.context || !this.moveBasePoint || !this.moveCurrentPoint) return;

        const translation = this.moveCurrentPoint.clone().sub(this.moveBasePoint);
        if (translation.lengthSq() <= 1e-9) return;

        const elements = this.context.bimStore.project.elements;
        const foreignIds = this.moveElementIds.filter((id) => !elements.has(id));

        // Cross-workbench members (CAD entities, sketch citizens) move as
        // one routed step: the edit router writes each object to its owning
        // store's undo chain.
        if (foreignIds.length > 0) {
            transformSceneObjects(
                foreignIds,
                new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z),
                'Move'
            );
        }

        const wallIds: string[] = [];
        const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];

        for (const id of this.moveElementIds) {
            const element = elements.get(id);
            if (!element) continue;

            // Walls take the joint-aware path: connected walls stretch along
            // their alignment and render geometry is re-solved in one batch.
            if (element.category === 'Walls') {
                wallIds.push(id);
                continue;
            }

            const mutator = this.getElementGeometryPoints(element);
            if (mutator) batch.push({ id, updates: mutator.apply(translation) });
        }

        if (wallIds.length > 0) {
            const wallUpdates = computeWallMoveUpdates(elements, wallIds, translation);
            wallUpdates.forEach((updates, id) => batch.push({ id, updates }));
        }

        // Single batched commit: one undo entry, one store refresh.
        if (batch.length > 0) {
            this.context.bimStore.updateElements(batch);
        }
    }

    private cancelMove(): void {
        // Geometry was never mutated during the drag (preview-only), so
        // resetting the gesture state is sufficient.
        this.resetMoveGesture();
        if (this.context) {
            this.context.appStore.setToolPreview(null);
        }
    }

    private isMovableElement(id: string): boolean {
        if (!this.context) return false;
        const element = this.context.bimStore.project.elements.get(id);
        if (element) {
            if (element.parameters?.['Pinned']) return false;
            if (element.category === 'Levels' || element.category === 'Grids') return false;
            return this.getElementGeometryPoints(element) !== null;
        }
        // Foreign citizens (CAD entities, sketch solids/edges/annotations)
        // drag through the cross-workbench edit router.
        return sceneSourceOf(id) !== null;
    }

    /**
     * Collect the mutable world-space points of an element's geometry so a
     * drag translation can be applied to whatever shape the category uses.
     */
    private getElementGeometryPoints(element: BIMElement): {
        apply: (translation: THREE.Vector3) => Partial<BIMElement>;
    } | null {
        const geometry = (element as any).geometryData;
        if (!geometry) return null;

        const toTriple = (v: THREE.Vector3): [number, number, number] => [v.x, v.y, v.z];

        // Linear elements (walls, beams, braces, wires, placeholders...)
        if (geometry.start && geometry.end) {
            const start = new THREE.Vector3(...geometry.start);
            const end = new THREE.Vector3(...geometry.end);
            return {
                apply: (t) => ({
                    geometryData: {
                        ...geometry,
                        start: toTriple(start.clone().add(t)),
                        end: toTriple(end.clone().add(t))
                    }
                })
            };
        }

        // Path-based elements (ducts, pipes, conduits, cables...)
        if (Array.isArray(geometry.path)) {
            const path = geometry.path.map((p: [number, number, number]) => new THREE.Vector3(...p));
            return {
                apply: (t) => ({
                    geometryData: {
                        ...geometry,
                        path: path.map((p: THREE.Vector3) => toTriple(p.clone().add(t)))
                    }
                })
            };
        }

        // Polyline / control-point based elements (flex ducts, flex pipes...)
        if (Array.isArray(geometry.controlPoints)) {
            const points = geometry.controlPoints.map((p: [number, number, number]) => new THREE.Vector3(...p));
            return {
                apply: (t) => ({
                    geometryData: {
                        ...geometry,
                        controlPoints: points.map((p: THREE.Vector3) => toTriple(p.clone().add(t)))
                    }
                })
            };
        }

        // Boundary-based elements (floors, roofs, areas, toposolids...)
        if (Array.isArray(geometry.boundary)) {
            const boundary = geometry.boundary.map((p: [number, number]) => new THREE.Vector3(p[0], p[1], 0));
            return {
                apply: (t) => ({
                    geometryData: {
                        ...geometry,
                        boundary: boundary.map((p: THREE.Vector3) => [p.x + t.x, p.y + t.y])
                    }
                })
            };
        }

        // Point-based elements (columns, fixtures, equipment, sprinklers...).
        // Must be an array: hosted elements (doors/windows) store `position`
        // as a scalar 0-1 parameter along the host wall and are not movable.
        if (Array.isArray(geometry.position)) {
            const position = new THREE.Vector3(...geometry.position);
            return {
                apply: (t) => ({
                    geometryData: {
                        ...geometry,
                        position: toTriple(position.clone().add(t))
                    }
                })
            };
        }

        return null;
    }

    private findElementAtPoint(event: ThreeEvent<PointerEvent>): string | null {
        // Cross-workbench picking (B4): the manual scene raycast hits the
        // foreign reference layers too (CAD entities, sketch citizens), so a
        // drawing drawn in another workbench resolves exactly like a native
        // BIM element — click, box select and drag-move all share the path.
        return this.findSceneObjectIdAtPoint(event);
    }

    /**
     * First selectable scene citizen under the pointer, front-to-back: a BIM
     * element when the hit resolves there, otherwise a foreign object
     * (CAD/sketch) through its registry tag — same lock/visibility gate the
     * shared click dispatch applies.
     */
    private findSceneObjectIdAtPoint(event: ThreeEvent<PointerEvent>): string | null {
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (this.isUnpickableHit(hit)) continue;
            const bimId = this.resolveElementIdFromHit(hit);
            if (bimId) return bimId;
            const foreignId = this.resolveForeignIdFromHit(hit);
            if (foreignId) return foreignId;
        }
        return null;
    }

    /** Resolve a foreign (non-BIM) scene citizen id from a hit by walking
     *  up to the registry-tagged root, gating on ledger visibility/lock. */
    private resolveForeignIdFromHit(hit: THREE.Intersection): string | null {
        let obj: THREE.Object3D | null = hit.object;
        while (obj) {
            const id = obj.userData?.entityId as string | undefined;
            if (id) {
                const sceneObject = useDocumentStore.getState().sceneObjects[id];
                if (sceneObject) {
                    if (!sceneObject.visible || sceneObject.locked) return null;
                    const layer = useAppStore.getState().layers[sceneObject.layerId];
                    if (layer?.locked) return null;
                }
                return sceneSourceOf(id) ? id : null;
            }
            obj = obj.parent;
        }
        return null;
    }

    /**
     * Intersect the pointer ray with the z=0 work plane (BIM-style plan
     * drag plane). Recomputes the ray from the native pointer position so the
     * first event after activation doesn't carry a stale camera ray.
     */
    private getWorkPlanePoint(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        if (!this.context) return null;
        const domElement = this.context.renderer.domElement;
        const rect = domElement.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return null;
        const pointerNdc = new THREE.Vector2(
            ((event.nativeEvent.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.nativeEvent.clientY - rect.top) / rect.height) * 2 + 1
        );
        this.context.camera.updateMatrixWorld();
        this.context.raycaster.setFromCamera(pointerNdc, this.context.camera);
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const point = new THREE.Vector3();
        return this.context.raycaster.ray.intersectPlane(plane, point) ? point : null;
    }

    private handleElementClick(elementId: string, event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const currentSelection = [...this.context.appStore.selectedIds];
        const isSelected = currentSelection.includes(elementId);
        const setSelection = (ids: string[]): void => {
            this.context!.appStore.select(ids);
            this.context!.bimStore.setSelection(ids);
        };

        // BIM modifier semantics: Shift is the dominant "remove from
        // selection" key (Ctrl+Shift behaves like Shift); Ctrl extends the
        // selection set; a plain click replaces it.
        if (event.shiftKey) {
            // Shift + click: deselect only; clicking an unselected element
            // does nothing (BIM parity).
            if (isSelected) {
                setSelection(currentSelection.filter(id => id !== elementId));
            }
        } else if (event.ctrlKey || event.metaKey) {
            // Ctrl + click: add to selection
            if (!isSelected) {
                setSelection([...currentSelection, elementId]);
            }
        } else {
            // Normal click: Replace selection
            setSelection([elementId]);
        }

        // Committing a click ends any live Tab-cycle preview
        if (this.cycleIndex >= 0 || this.cycleSteps.length > 0) {
            this.cycleSteps = [];
            this.cycleIndex = -1;
            this.context.appStore.setToolPreview(null);
        }
    }

    private completeBoxSelection(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.boxStartPoint || !this.boxCurrentPoint) return;

        const isCrossing = this.boxCurrentPoint.x < this.boxStartPoint.x;
        const selectedElements = this.getElementsInBox(
            this.boxStartPoint,
            this.boxCurrentPoint,
            isCrossing
        );

        const currentSelection = [...this.context.appStore.selectedIds];
        const setSelection = (ids: string[]): void => {
            this.context!.appStore.select(ids);
            this.context!.bimStore.setSelection(ids);
        };

        // BIM modifier semantics, Shift dominant: a Shift box removes every
        // previously selected element it covers; a Ctrl box extends the
        // selection set; Ctrl+Shift keeps the Shift (remove) behavior.
        if (event.shiftKey) {
            setSelection(currentSelection.filter(id => !selectedElements.includes(id)));
        } else if (event.ctrlKey || event.metaKey) {
            setSelection([...new Set([...currentSelection, ...selectedElements])]);
        } else {
            // Normal: Replace selection
            setSelection(selectedElements);
        }
    }

    private getElementsInBox(
        start: { x: number; y: number },
        end: { x: number; y: number },
        isCrossing: boolean
    ): string[] {
        if (!this.context) return [];

        const selectedIds: string[] = [];
        const camera = this.context.camera;
        const rect = this.context.renderer.domElement.getBoundingClientRect();

        // Normalize coordinates to NDC
        const minX = Math.min(start.x, end.x) / rect.width * 2 - 1;
        const maxX = Math.max(start.x, end.x) / rect.width * 2 - 1;
        const minY = -(Math.max(start.y, end.y) / rect.height * 2 - 1);
        const maxY = -(Math.min(start.y, end.y) / rect.height * 2 - 1);

        // Check all BIM elements
        this.context.bimStore.project.elements.forEach((element, id) => {
            const geometryData = (element as any).geometryData;
            if (!geometryData) return;

            // Get element bounds in screen space
            const bounds = this.getElementScreenBounds(element, camera, rect);
            if (!bounds) return;

            if (isCrossing) {
                // Crossing mode: Select if box intersects element
                if (this.boxIntersects(minX, maxX, minY, maxY, bounds)) {
                    selectedIds.push(id);
                }
            } else {
                // Window mode: Select only if element is fully inside box
                if (this.boxContains(minX, maxX, minY, maxY, bounds)) {
                    selectedIds.push(id);
                }
            }
        });

        // Cross-workbench sweep (B4): foreign reference layers (CAD entities,
        // sketch citizens) register their roots in the shared entity
        // registry; their world bounds project into the same NDC test. The
        // ledger's visibility/lock language filters hidden/locked objects.
        const ledger = useDocumentStore.getState().sceneObjects;
        const visInput = currentVisibilityInput();
        entityMeshRegistry.forEach((obj, id) => {
            const sceneObject = ledger[id];
            if (sceneObject) {
                const r = resolveVisibility(sceneObject, visInput);
                if (!r.visible || r.locked) return;
            }
            const box = new THREE.Box3().setFromObject(obj);
            if (box.isEmpty()) return;
            const bounds = this.projectedBoxBounds(box, camera);
            if (!bounds) return;
            if (isCrossing
                ? this.boxIntersects(minX, maxX, minY, maxY, bounds)
                : this.boxContains(minX, maxX, minY, maxY, bounds)) {
                selectedIds.push(id);
            }
        });

        return selectedIds;
    }

    /** Project a world-space box to NDC screen bounds; null when any corner
     *  sits behind the camera (the projected bounds would be garbage). */
    private projectedBoxBounds(
        box: THREE.Box3,
        camera: THREE.Camera
    ): { minX: number; maxX: number; minY: number; maxY: number } | null {
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (const x of [box.min.x, box.max.x]) {
            for (const y of [box.min.y, box.max.y]) {
                for (const z of [box.min.z, box.max.z]) {
                    const corner = new THREE.Vector3(x, y, z);
                    if (corner.clone().applyMatrix4(camera.matrixWorldInverse).z >= 0) return null;
                    const projected = corner.project(camera);
                    if (projected.x < minX) minX = projected.x;
                    if (projected.x > maxX) maxX = projected.x;
                    if (projected.y < minY) minY = projected.y;
                    if (projected.y > maxY) maxY = projected.y;
                }
            }
        }
        return { minX, maxX, minY, maxY };
    }

    private getElementScreenBounds(
        element: any,
        camera: THREE.Camera,
        rect: DOMRect
    ): { minX: number; maxX: number; minY: number; maxY: number } | null {
        const geometryData = element.geometryData;
        if (!geometryData) return null;

        const points: THREE.Vector3[] = [];

        // Collect representative points based on element type. Each coordinate
        // payload must be a real numeric array — persisted elements can carry
        // malformed geometry that would otherwise break the spread below.
        const asVec3 = (value: unknown): THREE.Vector3 | null =>
            Array.isArray(value) && value.length >= 3 ? new THREE.Vector3(value[0], value[1], value[2]) : null;

        if (asVec3(geometryData.start) && asVec3(geometryData.end)) {
            // Linear elements (walls, etc.)
            points.push(asVec3(geometryData.start)!, asVec3(geometryData.end)!);
        } else if (Array.isArray(geometryData.boundary)) {
            // Boundary-based elements (floors, roofs, etc.)
            geometryData.boundary.forEach((p: number[]) => {
                points.push(new THREE.Vector3(p[0], p[1], geometryData.baseOffset || 0));
            });
        } else if (asVec3(geometryData.position)) {
            // Point-based elements
            points.push(asVec3(geometryData.position)!);
        }

        if (points.length === 0) return null;

        // Project points to screen space
        const screenPoints = points.map(p => {
            const projected = p.clone().project(camera);
            return { x: projected.x, y: projected.y };
        });

        // Get bounds
        const minX = Math.min(...screenPoints.map(p => p.x));
        const maxX = Math.max(...screenPoints.map(p => p.x));
        const minY = Math.min(...screenPoints.map(p => p.y));
        const maxY = Math.max(...screenPoints.map(p => p.y));

        return { minX, maxX, minY, maxY };
    }

    private boxIntersects(
        boxMinX: number,
        boxMaxX: number,
        boxMinY: number,
        boxMaxY: number,
        bounds: { minX: number; maxX: number; minY: number; maxY: number }
    ): boolean {
        return !(
            bounds.maxX < boxMinX ||
            bounds.minX > boxMaxX ||
            bounds.maxY < boxMinY ||
            bounds.minY > boxMaxY
        );
    }

    private boxContains(
        boxMinX: number,
        boxMaxX: number,
        boxMinY: number,
        boxMaxY: number,
        bounds: { minX: number; maxX: number; minY: number; maxY: number }
    ): boolean {
        return (
            bounds.minX >= boxMinX &&
            bounds.maxX <= boxMaxX &&
            bounds.minY >= boxMinY &&
            bounds.maxY <= boxMaxY
        );
    }
}

export default SelectToolClass;
